import { Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core'
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser'
import { ActivatedRoute, Router } from '@angular/router'
import { ProScheApplicationService } from 'src/api/temporary-apis/prosche-application.service'
import { AuthService } from 'src/app/components/isbeauth/auth.service'
import {
  ActionButtonDisp,
  FooterButtonTextAlign
} from 'src/app/module/common-parts/under-btn-disp/under-btn-disp.interface'
import { environment } from 'src/environments/environment'
import { Subscription, interval, lastValueFrom } from 'rxjs'
import { MessageService } from 'src/app/components/message/message.service'

@Component({
  selector: 'app-prosche-migration-estimate',
  templateUrl: './prosche-migration-estimate.component.html',
  styleUrls: ['./prosche-migration-estimate.component.scss']
})
export class ProScheMigrationEstimateComponent implements OnInit, OnDestroy {
  // 画面タイトル・パンくず用ラベル
  readonly title = '「ProSche」御見積書確認'
  // 利用規約 iframe 表示フラグ（初期状態は申込み明細を表示）
  isShowTerms = false
  // 利用規約 同意チェックフラグ
  isTermsAgreed = false
  // 申込み確定後の完了表示フラグ
  isConfirmed = false
  // 申込み確定中フラグ（二重送信防止）
  isConfirming = false
  // カウントダウン秒数
  countdown = 10
  // カウントダウン購読
  private countdownSub?: Subscription
  // 同意エリア強調表示フラグ
  isConsentHighlightActive = false
  // 同意エリア強調用タイマーID
  private consentHighlightTimer: any
  // 同意エリアDOM要素参照
  @ViewChild('consentSection') consentSection?: ElementRef<HTMLElement>
  // 利用規約 URL（環境変数から取得）
  readonly riyoukiyakuUrl: SafeResourceUrl
  // 申込み明細表示用のURL
  estimateUrl: SafeResourceUrl
  // 申込み明細表示用URLリスト（複数件対応）
  estimateUrls: SafeResourceUrl[] = []
  // 表示中の見積インデックス
  activeEstimateIndex = 0
  // フッターのアクションボタン定義
  actionButtons: ActionButtonDisp[] = []
  // 御見積書確認URL取得中フラグ
  loadingEstimate = true
  // 申込み確認モーダル表示フラグ
  confirmDialogOpen = false
  // お申し込み済みフラグ（次へボタン無効化用）
  isAlreadyApplied = false
  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private proScheApplicationService: ProScheApplicationService,
    private authService: AuthService,
    private messageService: MessageService,
    private sanitizer: DomSanitizer
  ) {
    // 信頼できるURLのみをサニタイズ
    this.riyoukiyakuUrl = this.sanitizer.bypassSecurityTrustResourceUrl(environment.riyoukiyakuUrl)
  }
  customerCd: string = ''
  ngOnInit(): void {
    // 初期表示時は「次へ（利用規約確認）」ボタンのみ表示
    this.setButtonsForEstimate()
    this.customerCd = this.authService.AccountInfo?.customerCd
    if (!this.customerCd) {
      return
    }
    this.getProScheApplicationsEstimate()
  }

  ngOnDestroy(): void {
    this.clearCountdown()
    // 同意エリア強調用タイマーのクリア
    if (this.consentHighlightTimer) {
      clearTimeout(this.consentHighlightTimer)
      this.consentHighlightTimer = undefined
    }
  }

  async getProScheApplicationsEstimate(): Promise<void> {
    this.isAlreadyApplied = false
    const ret = await lastValueFrom(this.proScheApplicationService.getProScheMessage(this.customerCd))
    if (ret?.resultCode !== 0 || !ret.data?.myContractorStatus?.length) {
      this.messageService.error('申込み情報の取得に失敗しました')
      this.isAlreadyApplied = true
      this.updateNextButtonDisabled()
      return
    }
    if (!ret.data.myContractorStatus.find((x) => x.approvalDatetime === null)) {
      this.isAlreadyApplied = true
      this.updateNextButtonDisabled()
      this.messageService.error('お申し込み済みです')
    }

    this.estimateUrls = ret.data.myContractorStatus.map((item) =>
      this.sanitizer.bypassSecurityTrustResourceUrl(
        `${environment.proscheEstimateUrl}${item.stockMigrationUserAndStatusId}`
      )
    )
    this.activeEstimateIndex = 0
    this.estimateUrl = this.estimateUrls[this.activeEstimateIndex]
    this.loadingEstimate = false
  }

  selectEstimate(index: number): void {
    if (index < 0 || index >= this.estimateUrls.length) {
      return
    }
    this.activeEstimateIndex = index
    this.estimateUrl = this.estimateUrls[index]
  }
  /**
   * 「次へ（利用規約確認）」ボタン押下時処理
   * 申込み明細を非表示にして利用規約を表示する
   */
  onNext(): void {
    if (this.isAlreadyApplied) {
      return
    }
    console.log('onNext')
    // 利用規約表示に切り替え
    this.isShowTerms = true
    // 利用規約同意チェックは毎回リセット
    this.isTermsAgreed = false
    // 利用規約確認用のボタン（戻る／申込み確定）に切り替え
    this.setButtonsForTerms()
  }

  /**
   * 戻るボタン押下時処理
   * 利用規約表示をやめて申込み明細表示に戻す
   */
  onBack(): void {
    // 申込み明細表示に戻す
    this.isShowTerms = false
    // 利用規約同意チェックをリセット
    this.isTermsAgreed = false
    // ボタンを「次へ（利用規約確認）」のみの状態に戻す
    this.setButtonsForEstimate()
  }

  /**
   * 利用規約同意チェック変更時処理
   * @param checked チェックボックス状態
   */
  onChangeTermsAgreed(checked: boolean): void {
    // 利用規約同意状態を更新
    this.isTermsAgreed = checked
    // 同意状態変更時に「申込みを確定する」ボタンの状態を再評価
    this.updateConfirmButtonDisabled()
  }

  /**
   * 申込み確定ボタン押下時処理
   */
  onConfirm(): void {
    if (this.isConfirming || this.isConfirmed) {
      return
    }
    // 利用規約に同意していない場合は、同意エリアへスクロールして強調表示のみ行い、確定処理は実行しない
    if (!this.isTermsAgreed) {
      this.messageService.warning('申込みを確定するには、利用規約への同意が必要です')
      this.scrollToConsentAndHighlight()
      return
    }

    this.confirmDialogOpen = true
  }

  onConfirmApproval(): void {
    this.confirmDialogOpen = false

    if (this.isConfirming || this.isConfirmed) {
      return
    }

    this.isConfirming = true
    this.updateConfirmButtonDisabled()
    this.proScheApplicationService.approval(this.customerCd).subscribe({
      next: (ret) => {
        if (ret?.resultCode === 0) {
          this.isConfirmed = true
          this.setButtonsForConfirmed()
          this.startCountdown()
        } else {
          // エラー処理
          this.isConfirming = false
          this.updateConfirmButtonDisabled()
          this.messageService.error('申込み処理に失敗しました')
        }
      },
      error: () => {
        debugger
        this.isConfirming = false
        this.updateConfirmButtonDisabled()
      }
    })
  }

  /**
   * 「申込みを確定する」ボタンの disabled 制御更新
   */
  private updateConfirmButtonDisabled(): void {
    // ラベル名で対象ボタンを特定（将来 actionCd 等に変更しても対応しやすいように関数化）
    const confirmButton = this.actionButtons.find((button) => button.label === '申込みを確定する')
    if (confirmButton) {
      // 同意有無にかかわらず常に押下可能とし、API 処理中のみ二重送信防止のために非活性とする
      confirmButton.disabled = this.isConfirming
    }
  }

  /**
   * 申込み明細表示時のフッターボタン設定
   */
  private setButtonsForEstimate(): void {
    this.actionButtons = [
      {
        label: '次へ（利用規約確認）',
        disabled: this.isAlreadyApplied,
        actionFunction: () => this.onNext()
      }
    ]
  }

  private updateNextButtonDisabled(): void {
    const nextButton = this.actionButtons.find((button) => button.label === '次へ（利用規約確認）')
    if (nextButton) {
      nextButton.disabled = this.isAlreadyApplied
    }
  }

  /**
   * 利用規約表示時のフッターボタン設定（戻る／申込み確定）
   */
  private setButtonsForTerms(): void {
    this.actionButtons = [
      {
        label: '戻る',
        textAlign: FooterButtonTextAlign.LEFT,
        actionFunction: () => this.onBack()
      },
      {
        label: '申込みを確定する',
        // ボタン自体は常に押下可能とし、API 処理中のみ二重送信防止のために disabled にする
        disabled: this.isConfirming,
        actionFunction: () => this.onConfirm()
      }
    ]
  }

  /**
   * 申込み確定後のフッターボタン設定（すぐに戻る）
   */
  private setButtonsForConfirmed(): void {
    this.actionButtons = [
      {
        label: 'すぐに戻る',
        actionFunction: () => this.onImmediateBack()
      }
    ]
  }

  /**
   * 「すぐに戻る」押下時処理
   */
  onImmediateBack(): void {
    this.clearCountdown()
    this.router.navigateByUrl('/')
  }

  /**
   * カウントダウン開始
   */
  private startCountdown(): void {
    this.clearCountdown()
    this.countdown = 10
    // 1秒ごとにカウントダウンして0秒で遷移
    this.countdownSub = interval(1000).subscribe(() => {
      this.countdown -= 1
      if (this.countdown <= 0) {
        this.onImmediateBack()
      }
    })
  }

  /**
   * カウントダウン購読の解除
   */
  private clearCountdown(): void {
    this.countdownSub?.unsubscribe()
    this.countdownSub = undefined
  }

  /**
   * 同意エリアまでスクロールし、一定時間ピコピコ光らせて強調表示する
   */
  private scrollToConsentAndHighlight(): void {
    const element = this.consentSection?.nativeElement
    // 同意エリア要素が存在しない場合は何もしない
    if (!element) {
      return
    }

    // 同意エリアまでスムーズスクロール
    element.scrollIntoView({
      behavior: 'smooth',
      block: 'start'
    })

    // 既にタイマーが存在する場合はクリアしてから再設定
    if (this.consentHighlightTimer) {
      clearTimeout(this.consentHighlightTimer)
      this.consentHighlightTimer = undefined
    }

    // 強調表示をON
    this.isConsentHighlightActive = true

    // 一定時間後に強調表示をOFF（パフォーマンスに影響が出ない程度の短時間にする）
    this.consentHighlightTimer = setTimeout(() => {
      this.isConsentHighlightActive = false
      this.consentHighlightTimer = undefined
    }, 3500)
  }
}
